/* eslint-env browser */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { Box } from 'reflexbox';
import {
  Label,
  Textarea,
  Text,
  Message,
} from 'rebass';

import { inputInfoShape } from 'shared/addonInput';
import {
  isTank,
  isHealer,
  isUnsupportedSpec,
} from 'shared/character';
import SharedConfig from 'shared/config';

import config from 'frontend/config';
import actions from 'frontend/state/actions';

import { selectors as simcItemsSelectors } from 'frontend/state/reducers/simbot/simcItems';
import { selectors as formSelectors } from 'frontend/state/reducers/simbot/form';
import { selectors as simbotSelectors } from 'frontend/state/reducers/simbot';

import {
  ErrorText,
  If,
  Loader,
} from 'frontend/components';
import CharacterInfo from 'frontend/components/Simbot/CharacterInfo';
import SimcInputError from 'frontend/components/Simbot/SimcInputError';

const SimcUserInput = ({ text, loading, inputInfo, character, gear, invalidLoadouts, onFieldChange, error }) => {
  let newerVersionAvailable = false;
  if (text && inputInfo) {
    newerVersionAvailable = !inputInfo.validVersion && inputInfo.minVersionDiff;
  }

  return (
    <Box sm={12}>
      <Label>Copy/paste the text from the SimulationCraft addon.
        {' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://support.raidbots.com/article/54-installing-and-using-the-simulationcraft-addon"
        >
          How to install and use the SimC addon
        </a>
      </Label>
      <Textarea
        id="SimcUserInput-input"
        name="input"
        label=""
        value={text}
        onChange={e => onFieldChange('text', e.target.value)}
        onFocus={e => e.target.select()}
        onPaste={e => {
          // clear previous input if paste is from simc addon
          const paste = (e.clipboardData || window.clipboardData).getData('text');
          const firstLine = _.first(_.split(paste, '\n'));
          if (firstLine.match(/^# SimC Addon/)) {
            onFieldChange('text', '');
          }
        }}
        rows={6}
        color={config.color}
        backgroundColor={config.colors.primary}
        style={{
          fontFamily: 'monospace',
        }}
        mb={1}
      />
      <SimcInputError
        inputInfo={inputInfo}
      />
      <If
        condition={text}
        then={() => (
          <Box
            style={{ minHeight: 60 }}
          >
            <Loader
              loading={loading}
              description="Parsing SimC input..."
              loaded={() => (
                <If
                  condition={!_.isEmpty(character) && !_.isEmpty(gear)}
                  then={() => (
                    <CharacterInfo
                      character={character}
                      gear={gear}
                    />
                  )}
                />
              )}
            />
          </Box>
        )}
      />
      <If
        condition={newerVersionAvailable}
        then={() => (
          <Text color={config.colors.warning} bold>
            A new version of the
            {' '}
            <a
              href={SharedConfig.links.simcAddon}
              target="_blank"
              rel="noopener noreferrer"
            >
              SimulationCraft addon
            </a>
            {' '} is available
          </Text>
        )}
      />
      <If
        condition={error && error.type === 'invalid_commands'}
        then={() => (
          <ErrorText mb={2}>
            Invalid commands: {error.details.invalidCommands.map(c => c.key).join(', ')}
          </ErrorText>
        )}
      />
      <If
        condition={error && error.type === 'simc_invalid_input'}
        then={() => (
          <Box style={{ color: config.colors.red }}>
            <Text>Invalid SimC Input (check for copy/paste errors):</Text>
            <ul>
              {error.details.simcErrors.map(e => (
                <li
                  key={e}
                >
                  {e}
                </li>
              ))}
            </ul>
          </Box>
        )}
      />
      <If
        condition={invalidLoadouts.length > 0}
        then={() => (
          <ErrorText mb={2} fontSize={15}>
            Invalid/out-of-date talent loadouts found. These need to be updated/recreated:
            {' '}
            {_.join(_.map(invalidLoadouts, l => l.name), ', ')}
          </ErrorText>
        )}
      />
      <If
        condition={text && (isTank(character) || isHealer(character))}
        then={() => (
          <Text p={0} mb={0} theme="warning" mt={0}>
            Note: Tanks and Healers sim DPS, not healing or survival
          </Text>
        )}
      />
      <If
        condition={text && isUnsupportedSpec(character)}
        then={() => (
          <Message p={1} mb={0} theme="error" mt={1}>
            Your spec is not supported by Raidbots and/or SimulationCraft
          </Message>
        )}
      />
    </Box>
  );
};

SimcUserInput.propTypes = {
  text: PropTypes.string,
  loading: PropTypes.bool,
  onFieldChange: PropTypes.func,
  inputInfo: PropTypes.shape(inputInfoShape),
  character: PropTypes.object,
  gear: PropTypes.object,
  invalidLoadouts: PropTypes.array,
  error: PropTypes.shape({
    type: PropTypes.string,
    details: PropTypes.object,
  }),
};
SimcUserInput.defaultProps = {
  text: null,
  loading: false,
  onFieldChange: () => {},
  inputInfo: false,
  character: {},
  gear: {},
  invalidLoadouts: [],
  error: null,
};

const mapStateToProps = state => ({
  text: formSelectors.text(state),
  loading: simcItemsSelectors.loading(state),
  error: formSelectors.error(state),
  inputInfo: formSelectors.inputInfo(state),
  character: simbotSelectors.character(state),
  gear: simbotSelectors.equippedGear(state),
  invalidLoadouts: simbotSelectors.invalidLoadouts(state),
});

const mapDispatchToProps = {
  onFieldChange: actions.inputForm,
};

export default connect(mapStateToProps, mapDispatchToProps)(SimcUserInput);
